// Entry point: boots UI, starts loop, creates the world when a new game starts.
import { bus } from './core/bus.js';
import { store } from './core/store.js';
import { startLoop, onRender } from './core/loop.js';
import { assets } from './core/assets.js';
import { WorldRenderer } from './world/renderer.js';
import { Terrain } from './world/terrain.js';
import { FreeCamera } from './world/camera.js';
import { BuildSystem } from './systems/build.js';
import { AnimalSystem } from './systems/animals.js';
import { AmenitySystem, generateAmenityThumbnails } from './systems/amenities.js';
import { VisitorSystem } from './systems/visitors.js';
import { EconomySystem } from './systems/economy.js';
import { WeatherSystem } from './systems/weather.js';
import { ResearchSystem } from './systems/research.js';
import { onSimTick } from './core/loop.js';
import { addWater, addVegetation, addBackdrop } from './world/environment.js';
import { initBuildingKit } from './world/buildingKit.js';
import { EnclosureLabels } from './systems/enclosureLabels.js';
import { mountUI } from './ui/app.js';
import { saveGame, loadSlotData, applySave, b64ToHeights, AUTOSAVE_SLOT } from './systems/save.js';
import { MissionSystem } from './systems/missions.js';
import { GuideArrow } from './systems/guide.js';
import { FlowOverlay } from './systems/flow.js';
import { OverlaySystem } from './systems/overlays.js';
import { initAmbience } from './systems/ambience.js';
import { GameplaySystem } from './systems/gameplay.js';
import { PropSystem } from './systems/props.js';

let world = null; // { renderer, terrain, camera }

function disposeWorld() {
  if (!world) return;
  if (world._prewarmT) clearTimeout(world._prewarmT);
  [
    'overlays', 'encLabels', 'guide', 'flow', 'gameplay', 'props',
    'build', 'animals', 'amenities', 'visitors', 'economy', 'weather',
    'research', 'missions', 'camera', 'renderer',
  ].forEach((k) => {
    try { world[k]?.dispose?.(); } catch (e) { console.warn('[world] dispose failed', k, e); }
  });
  world = null;
}

async function createWorld(mapSize, seed, savedHeights, savedWaterLevel) {
  disposeWorld();
  await assets.preload((p) => bus.emit('loading:progress', p));
  await initBuildingKit();
  setTimeout(() => generateAmenityThumbnails(), 0); // build shop thumbnails once the kit is ready
  const container = document.getElementById('game-container');
  container.innerHTML = '';
  const renderer = new WorldRenderer(container);
  const terrain = new Terrain(mapSize, seed, savedHeights, savedWaterLevel);
  renderer.scene.add(terrain.mesh);
  const water = addWater(renderer.scene, terrain, renderer.sunPosition);
  renderer.water = water;
  const backdrop = addBackdrop(renderer.scene, terrain);
  const vegetation = await addVegetation(renderer.scene, terrain, 42);
  const camera = new FreeCamera(renderer.camera, renderer.renderer.domElement, terrain);
  world = { renderer, terrain, camera, water, vegetation };
  world.build = new BuildSystem(world);
  world.animals = new AnimalSystem(world, world.build);
  world.amenities = new AmenitySystem(world, world.build);
  world.visitors = new VisitorSystem(world, world.build, world.amenities, world.animals);
  if (world.encLabels) world.encLabels.dispose();
  world.encLabels = new EnclosureLabels(world);
  world.build.addPerimeter(world.visitors.gateCell.cx);
  world.economy = new EconomySystem(world);
  world.weather = new WeatherSystem(world);
  world.research = new ResearchSystem();
  world.visitors.research = world.research;
  world.animals.research = world.research;
  world.visitors.weather = world.weather;
  world.visitors.economy = world.economy;
  world.animals.economy = world.economy;
  world.missions = new MissionSystem(world);
  world.guide = new GuideArrow(world);
  world.flow = new FlowOverlay(world);
  if (world.gameplay) world.gameplay.dispose();
  world.gameplay = new GameplaySystem(world);
  if (world.props) world.props.dispose();
  world.props = new PropSystem(world, world.build);
  if (world.overlays) world.overlays.dispose();
  world.overlays = new OverlaySystem(world);
  // Pre-warm billboard impostors ~5s in (models loaded by then) so first far-views don't hitch.
  if (world._prewarmT) clearTimeout(world._prewarmT);
  world._prewarmT = setTimeout(() => { try { if (world.animals) world.animals.prewarmImpostors(); } catch (e) {} }, 5000);
  // BAKE: the generated world is static — freeze matrices so three.js skips
  // per-frame matrix/world updates for terrain, water, backdrop and all vegetation.
  [terrain.mesh, water, backdrop.mesh, vegetation].forEach((root) => {
    if (!root) return;
    root.traverse ? root.traverse((o) => { o.updateMatrix(); o.matrixAutoUpdate = false; }) : null;
  });
  window.__world = world; // debug handle
  if (vegetation && vegetation.userData.setDensity) vegetation.userData.setDensity(store.get().graphics ? (store.get().graphics.treeDensity ?? 1) : 1);
  return world;
}

async function startNewGame({ zooName, mapSize, kidsMode }) {
  store.set({ screen: 'loading', zooName, mapSize, kidsMode: !!kidsMode });
  await createWorld(mapSize);
  if (kidsMode) addStarterHabitat();
  store.set({ screen: 'game', speed: 0 });
  goToGate();
  bus.emit('game:started', { zooName, mapSize });
}

// Snap (and settle) the camera at the entrance gate, looking north into the zoo.
function goToGate() {
  if (!world) return;
  const gate = world.visitors.gateCell;
  const t = world.terrain;
  const half = t.worldSize / 2;
  world.camera.target.set((gate.cx + 0.5) * t.cellSize - half, 0, (gate.cz - 6) * t.cellSize - half);
  world.camera.distance = 70;
  world.camera.yaw = 0;          // camera sits south of the gate, looking north into the grounds
  world.camera.pitch = 0.62;
  world.camera.update(0);
  // run again next frame in case terrain height / layout settled after this tick
  requestAnimationFrame(() => world && world.camera.update(0));
}

// Kids mode: begin with a small ready-made flamingo habitat next to the gate,
// so the zoo is alive from second one and shows how a habitat works.
function addStarterHabitat() {
  const b = world.build;
  const gate = world.visitors.gateCell;
  const N = world.terrain.cells;
  const inset = world.terrain.perimeterInset ?? 0;
  const x0 = gate.cx + 4, x1 = gate.cx + 9;
  const z1 = N - inset - 4, z0 = z1 - 5;
  for (let x = x0; x <= x1; x++) { b.fences.set(`${x},${z0},H`, true); b.fences.set(`${x},${z1 + 1},H`, true); }
  for (let z = z0; z <= z1; z++) { b.fences.set(`${x0},${z},V`, true); b.fences.set(`${x1 + 1},${z},V`, true); }
  b._rebuildFences();
  b._detectEnclosures();
  const enc = b.enclosures.find((e) => e.cells.has(`${x0 + 2},${z0 + 2}`));
  if (enc) {
    const a1 = world.animals.spawn('flamingo', enc, x0 + 2, z0 + 2, 1);
    const a2 = world.animals.spawn('flamingo', enc, x0 + 3, z0 + 3, 1);
    a1.sex = 'f'; a2.sex = 'm';
  }
}

// Home button: fly the camera back to the gate.
bus.on('camera:home', () => goToGate());

async function loadGame(slot) {
  const data = loadSlotData(slot);
  if (!data) return;
  store.set({ screen: 'loading', zooName: data.state.zooName, mapSize: data.state.mapSize });
  await createWorld(data.state.mapSize, data.terrain.seed, b64ToHeights(data.terrain.heights), data.terrain.waterLevel);
  world.terrain.refresh();
  applySave(world, data);
  goToGate();
  store.set({ screen: 'game', speed: 0 });
}

bus.on('game:new', startNewGame);
initAmbience();
bus.on('game:load', loadGame);
// Bundled start scenario (e.g. "Golden Age"): load a preset save shipped as a JSON file.
async function fetchJsonWithRetry(url, attempts = 3) {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (error) {
      lastError = error;
      await new Promise((resolve) => setTimeout(resolve, 250 * (i + 1)));
    }
  }
  throw lastError;
}

async function loadScenario(url) {
  let data;
  try { data = await fetchJsonWithRetry(url); } catch (e) { console.warn('[scenario] load failed', url, e); return; }
  if (!data || !data.state) return;
  store.set({ screen: 'loading', zooName: data.state.zooName, mapSize: data.state.mapSize });
  await createWorld(data.state.mapSize, data.terrain.seed, b64ToHeights(data.terrain.heights), data.terrain.waterLevel);
  world.terrain.refresh();
  applySave(world, data);
  goToGate();
  store.set({ screen: 'game', speed: 0 });
}
bus.on('game:loadScenario', loadScenario);
bus.on('game:save', (slot) => { if (world) saveGame(world, slot); });

// Autosave: silently every 3rd in-game day, scheduled in idle time
let _autosaveDay = 0;
bus.on('sim:newDay', () => {
  if (!world || store.get().screen !== 'game') return;
  if (++_autosaveDay % 3 !== 0) return;
  const run = () => saveGame(world, AUTOSAVE_SLOT, true);
  if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 0);
});

onSimTick((dt) => {
  if (world) world.visitors.simTick(dt);
});

onRender((dt) => {
  if (!world || store.get().screen !== 'game') return;
  world.camera.update(dt);
  world.animals.update(dt);
  // publish fps to HUD once per second
  if (world.renderer.fps !== store.get().fps) store.set({ fps: world.renderer.fps });
  if (world.guide) world.guide.update(dt);
  if (world.flow) world.flow.update(dt);
  if (world.overlays) world.overlays.update(dt);
  if (world.amenities) world.amenities.updateLOD(world.renderer.camera);
  if (world.props) world.props.updateLOD(world.renderer.camera);
  if (world.encLabels) world.encLabels.update();
  if (world.water) world.water.material.uniforms.time.value += dt * 0.6;
  if (world.vegetation) {
    world.vegetation.userData.update(dt);
    world.vegetation.userData.updateLOD?.(world.renderer.camera);
    // Effective tree density = slider center + auto flex (±0.1, Auto mode only), applied when it moves.
    if (world.vegetation.userData.setDensity) {
      const g = store.get().graphics || {};
      const center = g.treeDensity == null ? 1 : g.treeDensity;
      const eff = Math.max(0.2, Math.min(1, center + (g.auto !== false ? (world.renderer.densityFlex || 0) : 0)));
      if (world._lastDensity == null || Math.abs(world._lastDensity - eff) > 0.001) { world.vegetation.userData.setDensity(eff); world._lastDensity = eff; }
    }
  }
  world.renderer.render(world.camera.target);
});

mountUI();
startLoop();

// Debug handles for performance investigation
import { pushNews } from './core/store.js';
window.__debug = { bus, store, pushNews };
